/**
 * Ship Points Service
 *
 * Multi-tenant aware service for CRUD operations on ship points (warehouses).
 * Ship points are stored in Firestore at: tenants/{tenantId}/ship_points
 *
 * A location can have multiple ship points (same physical warehouse,
 * different departments/purposes for Walmart).
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp
} from 'firebase/firestore';
import { FirebaseService } from '../../../shared/services/FirebaseService';

class ShipPointsServiceClass {
  constructor() {
    this.db = null;
    this.tenantId = null;
    this.initialized = false;
    console.log('🔧 ShipPointsService initialized');
  }

  /**
   * Set the current tenant context
   * @param {string} tenantId - The tenant ID (e.g., 'inmocean', 'acme')
   */
  setTenant(tenantId) {
    if (!tenantId) {
      throw new Error('tenantId is required');
    }
    this.tenantId = tenantId;
    console.log(`🏢 ShipPointsService tenant set to: ${tenantId}`);
  }

  /**
   * Get current tenant ID
   */
  getTenantId() {
    if (!this.tenantId) {
      throw new Error('Tenant not set. Call setTenant(tenantId) first.');
    }
    return this.tenantId;
  }

  /**
   * Initialize Firestore connection
   */
  async initialize() {
    if (this.initialized && this.db) return;

    try {
      await FirebaseService.initialize();
      this.db = FirebaseService.getFirestore();
      this.initialized = true;
      console.log('✅ ShipPointsService Firestore initialized');
    } catch (error) {
      console.error('❌ Failed to initialize ShipPointsService:', error);
      throw error;
    }
  }

  /**
   * Get the collection reference for current tenant's ship points
   */
  getShipPointsCollection() {
    const tenantId = this.getTenantId();
    return collection(this.db, `tenants/${tenantId}/ship_points`);
  }

  /**
   * Generate a unique ship point ID
   */
  generateShipPointId(code) {
    // Use code as ID if provided, otherwise generate
    if (code) {
      return code.toUpperCase().replace(/[^A-Z0-9]/g, '_');
    }
    return `sp_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
  }

  /**
   * Create a new ship point
   * @param {Object} shipPointData - The ship point data
   * @returns {Object} The created ship point with ID
   */
  async createShipPoint(shipPointData) {
    await this.initialize();
    const tenantId = this.getTenantId();

    try {
      console.log(`📦 Creating ship point for tenant ${tenantId}:`, shipPointData.code);

      const shipPointId = this.generateShipPointId(shipPointData.code);
      const currentUser = FirebaseService.getCurrentUser();

      const shipPoint = {
        id: shipPointId,
        code: (shipPointData.code || '').toUpperCase(),
        location: shipPointData.location || '',
        name: shipPointData.name || '',
        address: shipPointData.address || '',
        city: shipPointData.city || '',
        state: shipPointData.state || '',
        zip: shipPointData.zip || '',
        active: shipPointData.active !== false, // Default to true

        // Optional fields
        contactName: shipPointData.contactName || '',
        contactEmail: shipPointData.contactEmail || '',
        contactPhone: shipPointData.contactPhone || '',
        website: shipPointData.website || '',
        notes: shipPointData.notes || '',

        // Metadata
        created_by: currentUser?.email || 'unknown',
        created_at: serverTimestamp(),
        updated_by: currentUser?.email || 'unknown',
        updated_at: serverTimestamp()
      };

      const docRef = doc(this.db, `tenants/${tenantId}/ship_points`, shipPointId);
      await setDoc(docRef, shipPoint);

      console.log(`✅ Ship point created: ${shipPointId}`);
      return { ...shipPoint, id: shipPointId };

    } catch (error) {
      console.error('❌ Failed to create ship point:', error);
      throw new Error(`Failed to create ship point: ${error.message}`);
    }
  }

  /**
   * Update an existing ship point
   * @param {string} shipPointId - The ship point ID to update
   * @param {Object} updates - The fields to update
   * @returns {Object} The updated ship point
   */
  async updateShipPoint(shipPointId, updates) {
    await this.initialize();
    const tenantId = this.getTenantId();

    try {
      console.log(`📦 Updating ship point ${shipPointId} for tenant ${tenantId}`);

      const currentUser = FirebaseService.getCurrentUser();
      const docRef = doc(this.db, `tenants/${tenantId}/ship_points`, shipPointId);

      // Remove fields that shouldn't be updated
      const safeUpdates = { ...updates };
      delete safeUpdates.id;
      delete safeUpdates.created_by;
      delete safeUpdates.created_at;

      // Uppercase code if provided
      if (safeUpdates.code) {
        safeUpdates.code = safeUpdates.code.toUpperCase();
      }

      await updateDoc(docRef, {
        ...safeUpdates,
        updated_by: currentUser?.email || 'unknown',
        updated_at: serverTimestamp()
      });

      console.log(`✅ Ship point updated: ${shipPointId}`);
      return { id: shipPointId, ...safeUpdates };

    } catch (error) {
      console.error('❌ Failed to update ship point:', error);
      throw new Error(`Failed to update ship point: ${error.message}`);
    }
  }

  /**
   * Delete a ship point
   * @param {string} shipPointId - The ship point ID to delete
   */
  async deleteShipPoint(shipPointId) {
    await this.initialize();
    const tenantId = this.getTenantId();

    try {
      console.log(`🗑️ Deleting ship point ${shipPointId} for tenant ${tenantId}`);

      const docRef = doc(this.db, `tenants/${tenantId}/ship_points`, shipPointId);
      await deleteDoc(docRef);

      console.log(`✅ Ship point deleted: ${shipPointId}`);
      return { id: shipPointId, deleted: true };

    } catch (error) {
      console.error('❌ Failed to delete ship point:', error);
      throw new Error(`Failed to delete ship point: ${error.message}`);
    }
  }

  /**
   * Get a ship point by its ID
   * @param {string} shipPointId - The ship point ID
   * @returns {Object|null} The ship point or null if not found
   */
  async getShipPointById(shipPointId) {
    await this.initialize();
    const tenantId = this.getTenantId();

    try {
      const docRef = doc(this.db, `tenants/${tenantId}/ship_points`, shipPointId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return { id: docSnap.id, ...docSnap.data() };

    } catch (error) {
      console.error('❌ Failed to get ship point:', error);
      throw error;
    }
  }

  /**
   * Get all ship points for the current tenant
   * @returns {Array} Array of all ship points
   */
  async getAllShipPoints() {
    await this.initialize();
    const tenantId = this.getTenantId();

    try {
      console.log(`📋 Fetching all ship points for tenant ${tenantId}`);

      const shipPointsRef = this.getShipPointsCollection();
      const q = query(shipPointsRef, orderBy('code', 'asc'));
      const querySnapshot = await getDocs(q);

      const shipPoints = [];
      querySnapshot.forEach((doc) => {
        shipPoints.push({ id: doc.id, ...doc.data() });
      });

      console.log(`✅ Retrieved ${shipPoints.length} ship points`);
      return shipPoints;

    } catch (error) {
      console.error('❌ Failed to get all ship points:', error);
      throw error;
    }
  }

  /**
   * Get active ship points only
   * @returns {Array} Array of active ship points
   */
  async getActiveShipPoints() {
    const allShipPoints = await this.getAllShipPoints();
    return allShipPoints.filter(sp => sp.active !== false);
  }

  /**
   * Get unique locations (for location filter)
   * @returns {Array} Array of unique location names
   */
  async getLocations() {
    const shipPoints = await this.getAllShipPoints();
    const locations = [...new Set(shipPoints.map(sp => sp.location).filter(Boolean))];
    return locations.sort();
  }

  /**
   * Get ship points grouped by location
   * @returns {Object} Map of location -> ship points array
   */
  async getShipPointsByLocation() {
    const shipPoints = await this.getAllShipPoints();
    const grouped = {};

    shipPoints.forEach(sp => {
      const location = sp.location || 'Unknown';
      if (!grouped[location]) {
        grouped[location] = [];
      }
      grouped[location].push(sp);
    });

    return grouped;
  }

  /**
   * Subscribe to real-time updates for all ship points
   * @param {Function} callback - Callback function receiving ship points array
   * @returns {Function} Unsubscribe function
   */
  subscribeToShipPoints(callback) {
    if (!this.db) {
      console.error('❌ Firestore not initialized. Call initialize() first.');
      return () => {};
    }

    const tenantId = this.getTenantId();
    console.log(`🔔 Subscribing to ship points for tenant ${tenantId}`);

    const shipPointsRef = this.getShipPointsCollection();
    const q = query(shipPointsRef, orderBy('code', 'asc'));

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const shipPoints = [];
      querySnapshot.forEach((doc) => {
        shipPoints.push({ id: doc.id, ...doc.data() });
      });

      console.log(`🔄 Ship points updated: ${shipPoints.length} ship points`);
      callback(shipPoints);
    }, (error) => {
      console.error('❌ Error listening to ship points:', error);
    });

    return unsubscribe;
  }

  /**
   * Get ship points as a lookup map keyed by code
   * @returns {Object} Map of code -> ship point
   */
  async getShipPointsLookup() {
    const shipPoints = await this.getAllShipPoints();
    const lookup = {};

    shipPoints.forEach(sp => {
      if (sp.code) {
        lookup[sp.code] = sp;
      }
    });

    return lookup;
  }
}

// Export singleton instance
export const ShipPointsService = new ShipPointsServiceClass();

// Also export class for testing
export { ShipPointsServiceClass };
